import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
} from "react";
import { useTheme, alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { fetchMuestraFilesCached } from "../../utils/muestraFilesRequest";
import DrawerMuestraV2 from "./Drawer/DrawerMuestraV2";
import {
  getMovimientosDetallados,
  getMuestraById,
  getEscandallosByMuestraId,
  getEscandalloSubgruposMuestras,
  getMuestrasTipo,
  getMuestrasEstado,
  getTallas,
  getColoresMuestras,
  getProveedores,
  getMarcas,
  getEscandalloGrupos,
} from "../../api/Muestras/muestraApi";

import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import {
  TextField,
  Typography,
  Box,
  Paper,
  Select,
  MenuItem,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  Tooltip,
  CircularProgress,
  IconButton,
  Chip,
} from "@mui/material";
import LocationOnOutlined from "@mui/icons-material/LocationOnOutlined";
import Inventory2Outlined from "@mui/icons-material/Inventory2Outlined";
import ImageNotSupportedOutlined from "@mui/icons-material/ImageNotSupportedOutlined";
import FileThumbnails from "../Estimates/FileThumbnails";
import { SidebarContext } from "../Sidebar/SidebarProvider";
import { parseMuestraDate } from "../../utils/dateUtils";

import QrCodeScannerOutlinedIcon from "@mui/icons-material/QrCodeScannerOutlined";

const DEFAULT_MUESTRA_PLACE = "Sin ubicación";

const MuestrasUbicacion = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { contentMargin } = useContext(SidebarContext);
  const { t } = useTranslation();

  const [movimientos, setMovimientos] = useState([]);
  const [filteredMovimientos, setFilteredMovimientos] = useState([]);
  const [muestraImages, setMuestraImages] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFilters, setSelectedFilters] = useState(["all"]);
  const [loadingImages, setLoadingImages] = useState(true);

  const [openDrawer, setOpenDrawer] = useState(false);
  const [selectedMuestra, setSelectedMuestra] = useState(null);

  const [tiposMuestra, setTiposMuestra] = useState([]);
  const [tiposEstado, setTiposEstado] = useState([]);
  const [tiposTallas, setTiposTallas] = useState([]);
  const [tiposColores, setTiposColores] = useState([]);
  const [tiposProveedores, setTiposProveedores] = useState([]);
  const [tiposMarcas, setTiposMarcas] = useState([]);
  const [escandalloGrupos, setEscandalloGrupos] = useState([]);

  const filterOptions = useMemo(
    () => [
      { label: t("MuestrasUbicacionPage.filterOneMonth"), value: "oneMonth" },
      { label: t("MuestrasUbicacionPage.filterTwoMonths"), value: "twoMonths" },
      {
        label: t("MuestrasUbicacionPage.filterThreeMonths"),
        value: "threeMonths",
      },
      { label: t("MuestrasUbicacionPage.filterAll"), value: "all" },
    ],
    [t],
  );

  // 🔐 Verificar login y cargar movimientos
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }

    try {
      const decodedToken = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      if (decodedToken.exp < currentTime) {
        localStorage.removeItem("token");
        navigate("/login");
      }
    } catch (error) {
      console.error("Error decoding token:", error);
      navigate("/login");
    }

    const fetchMovimientos = async () => {
      try {
        const data = await getMovimientosDetallados();
        console.log("🟢 DATA DESDE API:", data);
        setMovimientos(data);
        setFilteredMovimientos(data);
      } catch (error) {
        console.error("❌ Error fetching movimientos detallados:", error);
      }
    };

    // 🚀 Si hay nuevos movimientos marcados en localStorage, actualiza
    if (localStorage.getItem("muestrasUbicacionNeedsRefresh") === "true") {
      fetchMovimientos();
      localStorage.setItem("muestrasUbicacionNeedsRefresh", "false");
    } else {
      fetchMovimientos();
    }
  }, [navigate]);

  useEffect(() => {
    const handleNuevoMovimiento = (event) => {
      const nuevoMovimiento = event.detail;
      console.log(
        "⚡ Nuevo movimiento recibido (evento global):",
        nuevoMovimiento,
      );

      setMovimientos((prev) => {
        const existe = prev.some(
          (mov) => mov.ID_MOVIMIENTO === nuevoMovimiento.ID_MOVIMIENTO,
        );
        if (existe) return prev;
        return [nuevoMovimiento, ...prev];
      });
    };

    window.addEventListener("nuevo-movimiento", handleNuevoMovimiento);

    return () =>
      window.removeEventListener("nuevo-movimiento", handleNuevoMovimiento);
  }, []);

  useEffect(() => {
    const loadCatalogos = async () => {
      try {
        const [
          tiposM,
          estados,
          tallasData,
          coloresData,
          proveedoresData,
          marcasData,
          gruposEscandallo,
        ] = await Promise.all([
          getMuestrasTipo(),
          getMuestrasEstado(),
          getTallas(),
          getColoresMuestras(),
          getProveedores(),
          getMarcas(),
          getEscandalloGrupos(),
        ]);

        setTiposMuestra(tiposM);
        setTiposEstado(estados);
        setTiposTallas(tallasData);
        setTiposColores(coloresData);
        setTiposProveedores(proveedoresData);
        setTiposMarcas(marcasData);
        setEscandalloGrupos(gruposEscandallo);
      } catch (err) {
        console.error("Error cargando catálogos para DrawerMuestraV2:", err);
      }
    };

    loadCatalogos();
  }, []);

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (
        event.key === "muestrasUbicacionNeedsRefresh" &&
        event.newValue === "true"
      ) {
        console.log("🔄 Refrescando movimientos por cambio en localStorage...");
        // Vuelve a pedir los movimientos al backend
        getMovimientosDetallados().then((data) => {
          setMovimientos(data);
          setFilteredMovimientos(data);
          localStorage.setItem("muestrasUbicacionNeedsRefresh", "false");
        });
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  // 🔍 Filtros
  const applyFilters = useCallback(() => {
    const today = new Date();
    const oneMonthAgo = new Date(today);
    const twoMonthsAgo = new Date(today);
    const threeMonthsAgo = new Date(today);

    oneMonthAgo.setMonth(today.getMonth() - 1);
    twoMonthsAgo.setMonth(today.getMonth() - 2);
    threeMonthsAgo.setMonth(today.getMonth() - 3);

    const filtered = movimientos.filter((mov) => {
      const movDate = new Date(mov.FECHA_ENVIO);
      const marca = mov.MARCA?.toLowerCase() || "";
      const muestra = mov.MUESTRA?.toLowerCase() || "";
      const placed = mov.PLACED?.toLowerCase() || "";
      const nombreCliente = mov.muestra?.NombreCliente?.toLowerCase() || "";
      const fecha = mov.FECHA_ENVIO
        ? new Date(mov.FECHA_ENVIO).toLocaleDateString("es-ES").toLowerCase()
        : "";

      const matchesSearch =
        marca.includes(searchTerm) ||
        muestra.includes(searchTerm) ||
        placed.includes(searchTerm) ||
        nombreCliente.includes(searchTerm) ||
        fecha.includes(searchTerm);

      if (!matchesSearch) return false;
      if (selectedFilters.includes("all")) return true;

      if (
        selectedFilters.includes("oneMonth") &&
        movDate >= oneMonthAgo &&
        movDate <= today
      )
        return true;
      if (
        selectedFilters.includes("twoMonths") &&
        movDate < oneMonthAgo &&
        movDate >= twoMonthsAgo
      )
        return true;
      if (
        selectedFilters.includes("threeMonths") &&
        movDate < twoMonthsAgo &&
        movDate >= threeMonthsAgo
      )
        return true;

      return false;
    });

    setFilteredMovimientos(filtered);
  }, [movimientos, searchTerm, selectedFilters]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const handleSearch = (event) =>
    setSearchTerm(event.target.value.toLowerCase());

  const handleFilterChange = (event) => {
    const { value } = event.target;
    const filters = typeof value === "string" ? value.split(",") : value;
    if (filters.includes("all")) setSelectedFilters(["all"]);
    else setSelectedFilters(filters.filter((f) => f !== "all"));
  };

  // 📸 Cargar fotos (idéntico a MuestraArchivosCell)
  useEffect(() => {
    if (!movimientos.length) return; // usa movimientos directamente, no el filtrado
    const fetchMuestraImages = async () => {
      setLoadingImages(true);
      const imageMap = {};

      const promises = movimientos.map(async (mov) => {
        const muestraId = mov.muestra?.ID_MUESTRA || mov.ID_MUESTRA;

        if (!muestraId) {
          console.warn("⛔ Sin muestra válida:", mov);
          return;
        }

        try {
          const files = await fetchMuestraFilesCached(muestraId);
          const imageFiles = files.filter((f) =>
            f.mimeType?.startsWith("image/"),
          );
          if (imageFiles.length > 0) {
            const fullPath = imageFiles[0].path.replace(/\\/g, "/");
            imageMap[muestraId] = {
              path: fullPath,
              mimeType: imageFiles[0].mimeType,
            };
          }
        } catch (error) {
          console.error(
            "❌ Error cargando archivos para muestra:",
            muestraId,
            error,
          );
        }
      });

      await Promise.all(promises);
      setMuestraImages(imageMap);
      setLoadingImages(false);
    };

    fetchMuestraImages();
  }, [movimientos]); // 👈 usamos 'movimientos', no 'filteredMovimientos'

  // Handler para abrir el drawer desde una fila de ubicación
  const parseDateOnly = parseMuestraDate;

  const handleOpenDrawerFromUbicacion = async (detalle) => {
    try {
      const muestraId = detalle.id; // viene de mov.ID_MUESTRA en getTreeData

      // 1. Cargar cabecera de la muestra
      const muestraData = await getMuestraById(muestraId);

      // 2. Cargar todas las líneas de escandallo
      const escData = await getEscandallosByMuestraId(muestraId);

      // 3. Para cada línea, cargar SUBGRUPOS según el grupo
      const escandallosFinal = [];

      for (const esc of escData) {
        let subgrupos = [];

        if (esc.ESCANDALLO_GRUPOS) {
          try {
            const res = await getEscandalloSubgruposMuestras(
              esc.ESCANDALLO_GRUPOS,
            );

            subgrupos = res.map((s) => ({
              ID_SUBGRUPO: s.ID_SUBGRUPO,
              SUBGRUPO: s.SUBGRUPO,
            }));
          } catch (err) {
            console.error("Error loading subgroups:", err);
          }
        }

        escandallosFinal.push({
          Escandallo_Id:
            esc.Escandallo_Id ??
            esc.ID_ESCANDALLO ??
            esc.Id_Escandallo ??
            esc.id_escandallo ??
            null,

          ESCANDALLO_GRUPOS: esc.ESCANDALLO_GRUPOS || "",
          ESCANDALLO_SUBGRUPO: esc.ESCANDALLO_SUBGRUPO || "",
          PROVEEDOR_ESCANDALLO: esc.Proveedor || "",
          UN: esc.Un || "",
          COSTE: esc.Coste || "",
          SUBGRUPOS_LIST: subgrupos,
        });
      }

      // 4. Pasamos la data al drawer (igual que en Muestras1)
      setSelectedMuestra({
        ...muestraData,
        NombreCliente: muestraData.NombreCliente || "Desconocido",
        FECHA_PREVISTA: parseDateOnly(muestraData.FECHA_PREVISTA),
        FECHA_GET_SUPPLIER: parseDateOnly(muestraData.FECHA_GET_SUPPLIER),
        FECHA_PETICION: parseDateOnly(muestraData.FECHA_PETICION),
        FECHA_ENVIO: parseDateOnly(muestraData.FECHA_ENVIO),
        FECHA_RECEPCION: parseDateOnly(muestraData.FECHA_RECEPCION),
        FECHA_PREVISTA_FABRICA: parseDateOnly(
          muestraData.FECHA_PREVISTA_FABRICA,
        ),
        ESCANDALLOS: escandallosFinal,
        ESCANDALLO_SUBGRUPOS_LIST: escandallosFinal[0]?.SUBGRUPOS_LIST || [],
      });

      setOpenDrawer(true);
    } catch (err) {
      console.error(
        "Error abriendo DrawerMuestraV2 desde MuestrasUbicacion:",
        err,
      );
    }
  };

  // 📦 Agrupar por Marca
  const getTreeData = () => {
    const clientes = {};

    filteredMovimientos.forEach((mov) => {
      const nombreUbicacion = mov.PLACED || DEFAULT_MUESTRA_PLACE;

      // 👇 ESTA LÍNEA ES OBLIGATORIA
      if (!clientes[nombreUbicacion]) clientes[nombreUbicacion] = [];

      clientes[nombreUbicacion].push({
        muestra: mov.MUESTRA || "Muestra Desconocida",
        nombreCliente: mov.NombreCliente || "",
        fecha: mov.FECHA_ENVIO,
        placed: mov.PLACED || "",
        id: mov.ID_MUESTRA,
        temporada: mov.TEMPORADA || "SS2026",
        marca: mov.MARCA || "",
        disenador: mov.DISENADOR || "",
        producto: mov.PRODUCT_NAME || "Producto",
      });
    });

    return Object.entries(clientes)
      .map(([ubicacion, data]) => ({
        ubicacion,
        detalles: data.sort((a, b) => new Date(b.fecha) - new Date(a.fecha)),
      }))
      .sort((a, b) => a.ubicacion.localeCompare(b.ubicacion));
  };

  const treeData = getTreeData();

  const getDateAgeSx = (fecha) => {
    if (!fecha) {
      return {
        bgcolor: "action.selected",
        color: "text.secondary",
        border: "1px solid",
        borderColor: "divider",
        fontWeight: 600,
      };
    }
    const d = new Date(fecha);
    const today = new Date();
    const oneMonthAgo = new Date(today);
    const twoMonthsAgo = new Date(today);
    const threeMonthsAgo = new Date(today);
    oneMonthAgo.setMonth(today.getMonth() - 1);
    twoMonthsAgo.setMonth(today.getMonth() - 2);
    threeMonthsAgo.setMonth(today.getMonth() - 3);

    if (d < threeMonthsAgo) {
      return {
        bgcolor: alpha(theme.palette.error.main, 0.12),
        color: "error.main",
        border: "1px solid",
        borderColor: alpha(theme.palette.error.main, 0.35),
        fontWeight: 600,
      };
    }
    if (d < twoMonthsAgo) {
      return {
        bgcolor: alpha(theme.palette.warning.main, 0.12),
        color: "warning.dark",
        border: "1px solid",
        borderColor: alpha(theme.palette.warning.main, 0.35),
        fontWeight: 600,
      };
    }
    if (d < oneMonthAgo) {
      return {
        bgcolor: alpha("#e6b800", theme.palette.mode === "dark" ? 0.18 : 0.2),
        color: theme.palette.mode === "dark" ? "warning.light" : "#5c4a00",
        border: "1px solid",
        borderColor: alpha("#e6b800", 0.45),
        fontWeight: 600,
      };
    }
    return {
      bgcolor: alpha(theme.palette.info.main, 0.12),
      color: "info.main",
      border: "1px solid",
      borderColor: alpha(theme.palette.info.main, 0.3),
      fontWeight: 600,
    };
  };

  const formatLocationTitle = (ubic) =>
    ubic === DEFAULT_MUESTRA_PLACE
      ? t("MuestrasUbicacionPage.unknownLocation")
      : ubic;

  const filterInputSx = {
    minWidth: 0,
    flex: { xs: "1 1 100%", sm: "1 1 140px" },
    maxWidth: { xs: "100%", sm: 200 },
    "& .MuiInputBase-root": {
      backgroundColor: theme.palette.caja.main,
      borderRadius: 2,
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.divider,
    },
    "& .MuiInputLabel-root": { color: theme.palette.text.secondary },
    "& .MuiInputBase-input::placeholder": {
      color: theme.palette.text.secondary,
      opacity: 0.9,
    },
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100dvh",
        boxSizing: "border-box",
        overflow: "hidden",
        pt: 2,
        pr: { xs: 1.5, md: 3 },
        pb: 2,
        ...contentMargin,
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 880,
          alignSelf: "center",
          pl: 2,
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            mb: 2,
            p: { xs: 1.5, sm: 2 },
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            flexShrink: 0,
            background:
              "linear-gradient(135deg, rgba(0,188,212,0.12) 0%, rgba(103,58,183,0.1) 100%)",
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 2px 12px rgba(0,0,0,0.35)"
                : "0 2px 12px rgba(0,0,0,0.06)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: { xs: 1.5, md: 2 },
              alignItems: { md: "flex-start" },
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ flexShrink: 0, minWidth: 0 }}>
              <Typography
                variant="overline"
                sx={{ opacity: 0.85, letterSpacing: 1, display: "block" }}
              >
                CRM
              </Typography>
              <Typography
                variant="h5"
                sx={{ fontWeight: 800, lineHeight: 1.2 }}
              >
                {t("sidebar.samplesLocation")}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                {t("MuestrasUbicacionPage.heroSubtitle")}
              </Typography>
            </Box>
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                display: "flex",
                flexWrap: "wrap",
                gap: 1.25,
                alignItems: "center",
                alignContent: "flex-start",
              }}
            >
              <TextField
                type="search"
                size="small"
                variant="outlined"
                placeholder={t("MuestrasUbicacionPage.searchPlaceholder")}
                value={searchTerm}
                onChange={handleSearch}
                inputProps={{
                  "aria-label": t("MuestrasUbicacionPage.searchPlaceholder"),
                }}
                sx={filterInputSx}
              />
              <FormControl size="small" variant="outlined" sx={filterInputSx}>
                <InputLabel id="muestras-ubicacion-filter-date">
                  {t("MuestrasUbicacionPage.filterByDate")}
                </InputLabel>
                <Select
                  labelId="muestras-ubicacion-filter-date"
                  label={t("MuestrasUbicacionPage.filterByDate")}
                  multiple
                  value={selectedFilters}
                  onChange={handleFilterChange}
                  renderValue={(selected) =>
                    selected
                      .map(
                        (filter) =>
                          filterOptions.find((f) => f.value === filter)?.label,
                      )
                      .join(", ")
                  }
                >
                  {filterOptions.map((filter) => (
                    <MenuItem key={filter.value} value={filter.value}>
                      <Checkbox
                        checked={selectedFilters.includes(filter.value)}
                      />
                      <ListItemText primary={filter.label} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Tooltip title={t("MuestrasUbicacionPage.makeMovement")}>
                <IconButton
                  onClick={() => navigate("/muestrasMovimientoCamara")}
                  size="small"
                  color="error"
                  sx={{
                    border: 1,
                    borderColor: "error.main",
                    flexShrink: 0,
                    bgcolor: alpha(
                      theme.palette.common.white,
                      theme.palette.mode === "dark" ? 0.06 : 0.65,
                    ),
                    "&:hover": {
                      bgcolor: alpha(
                        theme.palette.common.white,
                        theme.palette.mode === "dark" ? 0.12 : 0.9,
                      ),
                    },
                  }}
                >
                  <QrCodeScannerOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.5, sm: 2 },
            pr: { xs: 1, sm: 1.25 },
            flex: 1,
            minHeight: 0,
            overflowY: "scroll",
            overflowX: "hidden",
            WebkitOverflowScrolling: "touch",
            backgroundColor: theme.palette.background.paper,
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 1px 8px rgba(0,0,0,0.4)"
                : "0 1px 6px rgba(0,0,0,0.04)",
            scrollbarWidth: "thin",
            scrollbarColor: `${alpha(theme.palette.text.secondary, 0.55)} ${alpha(
              theme.palette.divider,
              theme.palette.mode === "dark" ? 0.35 : 0.5,
            )}`,
            "&::-webkit-scrollbar": {
              width: 12,
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: alpha(
                theme.palette.divider,
                theme.palette.mode === "dark" ? 0.35 : 0.45,
              ),
              borderRadius: 6,
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: alpha(theme.palette.text.secondary, 0.5),
              borderRadius: 6,
              border: `2px solid ${alpha(theme.palette.background.paper, 0.85)}`,
            },
            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: alpha(theme.palette.text.secondary, 0.72),
            },
          }}
        >
          {loadingImages ? (
            <Box
              sx={{
                textAlign: "center",
                py: 5,
                px: 2,
                minHeight: 200,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
              }}
            >
              <CircularProgress size={40} />
              <Typography color="text.secondary" variant="body2">
                {t("MuestrasUbicacionPage.loadingPhotos")}
              </Typography>
            </Box>
          ) : (
            <>
              {treeData.map((ubicacionData, index) => {
                const nSamples = ubicacionData.detalles.length;
                return (
                  <Paper
                    key={ubicacionData.ubicacion + String(index)}
                    variant="outlined"
                    sx={{
                      mb: 2.5,
                      overflow: "hidden",
                      borderRadius: 2,
                      borderLeft: 4,
                      borderLeftColor: "primary.main",
                      boxShadow: "none",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: 1,
                        px: 1.5,
                        py: 1.25,
                        bgcolor: alpha(theme.palette.primary.main, 0.04),
                        borderBottom: 1,
                        borderColor: "divider",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          minWidth: 0,
                        }}
                      >
                        <LocationOnOutlined
                          color="primary"
                          sx={{ fontSize: 22, flexShrink: 0 }}
                        />
                        <Typography
                          variant="subtitle1"
                          fontWeight={800}
                          noWrap
                          sx={{ letterSpacing: 0.2 }}
                        >
                          {formatLocationTitle(ubicacionData.ubicacion)}
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        label={t("MuestrasUbicacionPage.sampleCount", {
                          count: nSamples,
                        })}
                        variant="outlined"
                        color="primary"
                        sx={{ fontWeight: 600, height: 24 }}
                      />
                    </Box>

                    <Box sx={{ p: 1.5, pt: 1.5 }}>
                      {ubicacionData.detalles.map((detalle, idx) => {
                        const imagePath = muestraImages[detalle.id];
                        return (
                          <Box
                            key={idx}
                            onClick={() =>
                              handleOpenDrawerFromUbicacion(detalle)
                            }
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                handleOpenDrawerFromUbicacion(detalle);
                              }
                            }}
                            sx={{
                              p: 1.5,
                              pl: 1.25,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 2,
                              borderRadius: 2,
                              border: "1px solid",
                              borderColor: "divider",
                              mb: 1.25,
                              cursor: "pointer",
                              transition:
                                "box-shadow 0.2s ease, border-color 0.2s ease, background-color 0.2s ease",
                              bgcolor: "background.paper",
                              "&:last-of-type": { mb: 0 },
                              "&:hover": {
                                bgcolor: "action.hover",
                                boxShadow: 1,
                                borderColor: alpha(
                                  theme.palette.primary.main,
                                  0.45,
                                ),
                              },
                              "&:focus-visible": {
                                outline: `2px solid ${theme.palette.primary.main}`,
                                outlineOffset: 2,
                              },
                            }}
                          >
                            {imagePath ? (
                              <Tooltip title={detalle.muestra}>
                                <Box
                                  sx={{
                                    width: 64,
                                    height: 64,
                                    borderRadius: 1.5,
                                    overflow: "hidden",
                                    flexShrink: 0,
                                    boxShadow: 1,
                                    border: "1px solid",
                                    borderColor: "divider",
                                  }}
                                >
                                  <FileThumbnails
                                    filePath={imagePath?.path}
                                    mimeType={imagePath?.mimeType}
                                  />
                                </Box>
                              </Tooltip>
                            ) : (
                              <Box
                                sx={{
                                  width: 64,
                                  height: 64,
                                  borderRadius: 1.5,
                                  border: "1px dashed",
                                  borderColor: "divider",
                                  bgcolor: "action.hover",
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: 0.25,
                                  flexShrink: 0,
                                }}
                              >
                                <ImageNotSupportedOutlined
                                  sx={{ fontSize: 22, color: "text.disabled" }}
                                />
                                <Typography
                                  variant="caption"
                                  color="text.disabled"
                                  sx={{
                                    lineHeight: 1,
                                    fontSize: "0.65rem",
                                    px: 0.5,
                                    textAlign: "center",
                                  }}
                                >
                                  {t("MuestrasUbicacionPage.noPhoto")}
                                </Typography>
                              </Box>
                            )}

                            <Box
                              sx={{
                                flex: 1,
                                minWidth: 0,
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                gap: 1.5,
                              }}
                            >
                              <Box sx={{ minWidth: 0 }}>
                                {!!detalle.nombreCliente && (
                                  <Typography
                                    variant="body2"
                                    fontWeight={600}
                                    noWrap
                                    color="text.secondary"
                                  >
                                    {detalle.nombreCliente}
                                  </Typography>
                                )}
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "row",
                                    flexWrap: "wrap",
                                    gap: 0.5,
                                    alignItems: "baseline",
                                  }}
                                >
                                  <Typography
                                    fontWeight={700}
                                    variant="body2"
                                    noWrap
                                    color="text.primary"
                                  >
                                    {detalle.muestra}
                                  </Typography>
                                  {!!detalle.temporada && (
                                    <Typography
                                      fontWeight={600}
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      · {detalle.temporada}
                                    </Typography>
                                  )}
                                </Box>
                                {!!detalle.marca && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: "block", mt: 0.25 }}
                                    noWrap
                                  >
                                    {detalle.marca}
                                  </Typography>
                                )}
                              </Box>
                              <Chip
                                size="small"
                                label={
                                  detalle.fecha
                                    ? new Date(
                                        detalle.fecha,
                                      ).toLocaleDateString("es-ES", {
                                        day: "2-digit",
                                        month: "2-digit",
                                        year: "numeric",
                                      })
                                    : t("MuestrasUbicacionPage.noDate")
                                }
                                sx={getDateAgeSx(detalle.fecha)}
                              />
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </Paper>
                );
              })}

              {treeData.length === 0 && (
                <Box
                  sx={{
                    textAlign: "center",
                    py: 6,
                    px: 2,
                  }}
                >
                  <Inventory2Outlined
                    sx={{
                      fontSize: 48,
                      color: "action.disabled",
                      opacity: 0.55,
                      mb: 1,
                    }}
                  />
                  <Typography
                    variant="subtitle1"
                    color="text.secondary"
                    fontWeight={600}
                  >
                    {t("MuestrasUbicacionPage.emptyStateTitle")}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.disabled"
                    sx={{ mt: 0.5, maxWidth: 400, mx: "auto" }}
                  >
                    {t("MuestrasUbicacionPage.emptyStateBody")}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Paper>
      </Box>
      {selectedMuestra && (
        <DrawerMuestraV2
          open={openDrawer}
          onClose={() => setOpenDrawer(false)}
          newMuestra={selectedMuestra}
          setNewMuestra={setSelectedMuestra}
          onSaveMuestra={async () => {
            // si quieres refrescar después de guardar
            const data = await getMovimientosDetallados();
            setMovimientos(data);
            setFilteredMovimientos(data);
          }}
          tiposMuestra={tiposMuestra}
          tiposEstado={tiposEstado}
          tiposTallas={tiposTallas}
          tiposColores={tiposColores}
          tiposMarcas={tiposMarcas}
          tiposProveedores={tiposProveedores}
          escandalloGrupos={escandalloGrupos}
          escandalloSubgrupos={selectedMuestra?.ESCANDALLO_SUBGRUPOS_LIST || []}
          escandallosIniciales={selectedMuestra.ESCANDALLOS}
          isEditMode={true}
          isFromMuestras={true}
          onRefresh={async () => {
            const data = await getMovimientosDetallados();
            setMovimientos(data);
            setFilteredMovimientos(data);
          }}
          onDeleteMuestra={(id) => {
            setMovimientos((prev) => prev.filter((m) => m.ID_MUESTRA !== id));
            setFilteredMovimientos((prev) =>
              prev.filter((m) => m.ID_MUESTRA !== id),
            );
          }}
        />
      )}
    </Box>
  );
};

export default MuestrasUbicacion;
